import { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Push the visitor's choice to Google Consent Mode v2. Safe to call even if
// gtag failed to load (the dataLayer queue swallows the call).
function applyConsent({ analytics, marketing }) {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
    function gtag(){ window.dataLayer.push(arguments); }
    gtag('consent', 'update', {
        analytics_storage: analytics ? 'granted' : 'denied',
        ad_storage: marketing ? 'granted' : 'denied',
        ad_user_data: marketing ? 'granted' : 'denied',
        ad_personalization: marketing ? 'granted' : 'denied',
    });
}

export function CookieConsent() {
    const { t } = useLanguage();
    const [visible, setVisible] = useState(false);
    const [showManage, setShowManage] = useState(false);
    const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

    useEffect(() => {
        const consent = localStorage.getItem('sleepstack_cookie_consent');
        if (!consent) {
            setTimeout(() => setVisible(true), 1500);
        }
    }, []);

    const acceptAll = () => {
        const choice = { analytics: true, marketing: true };
        localStorage.setItem('sleepstack_cookie_consent', JSON.stringify(choice));
        applyConsent(choice);
        setVisible(false);
    };

    const rejectAll = () => {
        const choice = { analytics: false, marketing: false };
        localStorage.setItem('sleepstack_cookie_consent', JSON.stringify(choice));
        applyConsent(choice);
        setVisible(false);
    };

    const savePrefs = () => {
        localStorage.setItem('sleepstack_cookie_consent', JSON.stringify(prefs));
        applyConsent(prefs);
        setVisible(false);
    };

    return (
        <AnimatePresence>
            {visible && (
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 40 }}
                    transition={{ duration: 0.3 }}
                    className="fixed bottom-3 left-3 right-3 md:bottom-4 md:left-auto md:right-6 md:max-w-sm z-50 bg-white border border-border rounded-2xl shadow-[0_8px_40px_rgba(0,0,0,0.12)] p-4 md:p-6"
                    data-testid="cookie-banner"
                >
                    <div className="flex justify-between items-start mb-2 md:mb-3">
                        <h3 className="font-heading text-base md:text-lg text-foreground">{t('cookie.title')}</h3>
                        <button
                            onClick={() => setVisible(false)}
                            className="text-muted-foreground hover:text-foreground transition-colors"
                            aria-label="Close"
                        >
                            <X size={18} />
                        </button>
                    </div>

                    <p className="text-xs md:text-sm text-muted-foreground leading-relaxed mb-3 md:mb-4">
                        {t('cookie.desc')}
                    </p>

                    {showManage && (
                        <div className="mb-4 space-y-3 border-t border-border pt-4">
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-foreground">{t('cookie.necessary')}</span>
                                <span className="text-xs text-muted-foreground">{t('cookie.always_on')}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-foreground">{t('cookie.analytics')}</span>
                                <button
                                    onClick={() => setPrefs(p => ({ ...p, analytics: !p.analytics }))}
                                    data-testid="cookie-analytics-toggle"
                                    className={`relative inline-flex h-6 w-11 rounded-full transition-colors duration-200 ${prefs.analytics ? 'bg-primary' : 'bg-muted'}`}
                                >
                                    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform duration-200 ${prefs.analytics ? 'translate-x-5' : 'translate-x-0.5'}`} />
                                </button>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-foreground">{t('cookie.marketing')}</span>
                                <button
                                    onClick={() => setPrefs(p => ({ ...p, marketing: !p.marketing }))}
                                    data-testid="cookie-marketing-toggle"
                                    className={`relative inline-flex h-6 w-11 rounded-full transition-colors duration-200 ${prefs.marketing ? 'bg-primary' : 'bg-muted'}`}
                                >
                                    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform duration-200 ${prefs.marketing ? 'translate-x-5' : 'translate-x-0.5'}`} />
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="flex flex-col gap-2">
                        <div className="flex gap-2">
                            <button
                                onClick={acceptAll}
                                data-testid="cookie-accept-all"
                                className="flex-1 btn-secondary text-xs md:text-sm py-2 md:py-2.5"
                            >
                                {t('cookie.accept')}
                            </button>
                            <button
                                onClick={rejectAll}
                                data-testid="cookie-reject-all"
                                className="flex-1 btn-ghost text-xs md:text-sm py-2 md:py-2.5"
                            >
                                {t('cookie.reject')}
                            </button>
                        </div>
                        {showManage ? (
                            <button
                                onClick={savePrefs}
                                data-testid="cookie-save-prefs"
                                className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 self-center mt-1"
                            >
                                {t('cookie.save')}
                            </button>
                        ) : (
                            <button
                                onClick={() => setShowManage(true)}
                                data-testid="cookie-manage"
                                className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 self-center mt-1"
                            >
                                {t('cookie.manage')}
                            </button>
                        )}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
